// src/pages/HomePage.js
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  ScrollParallax,
  MouseParallax,
  RotateOnScroll,
  ScaleOnScroll
} from '../components/parallax';
import { useChatWidget } from '../hooks/useChatWidget';
import Tooltip from '../components/Tooltip';
import Footer from '../components/Footer';
import { getExtendedDeviceProfile } from '../utils/layoutHelpers';

import { getNavigationConfig, CALENDLY_CONFIGS, getCalendlyPosition } from '../config/buttonPositions';
import { getWidgetConfig, getWidgetTitle } from '../config/widgetPositions';
import { getRoomBySlug, VF_ROOM_BY_PAGE } from '../config/rooms';

const ImageLayer = ({
  src,
  alt,
  className = "",
  style = {},
  isMovable = false,
  movementType = "float",
  zIndex = 1,
  parallaxIntensity = 0,
  onClick = null,
  onLoad = null,
  loading = "lazy",
  fetchPriority = "auto"
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
    if (onLoad) onLoad();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getMovementClass = () => {
    if (!isMovable) return "";
    switch (movementType) {
      case "float": return "animate-float";
      case "pulse": return "animate-pulse";
      case "bounce": return "animate-bounce";
      case "spin": return "animate-spin";
      case "ping": return "animate-ping";
      default: return "";
    }
  };

  const imageContent = (
    <img
      src={src}
      alt={alt}
      className={`transition-opacity duration-300 ${imageLoaded && !imageError ? 'opacity-100' : 'opacity-0'} ${className} ${getMovementClass()}`}
      style={{
        ...style,
        zIndex,
        cursor: onClick ? 'pointer' : 'default'
      }}
      onLoad={handleImageLoad}
      onError={handleImageError}
      onClick={onClick}
      loading={loading}
      fetchPriority={fetchPriority}
    />
  );

  if (parallaxIntensity !== 0) {
    return (
      <MouseParallax intensity={parallaxIntensity}>
        {imageContent}
      </MouseParallax>
    );
  }

  return imageContent;
};

const PageLayers = ({
  pageConfig,
  isActive,
  isTransitioning,
  currentPage,
  children,
  isLandscape
}) => {
  const { backgroundImage, layers = [] } = pageConfig;

  return (
    <div className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden bg-white">
      {backgroundImage && (
        <ImageLayer
          src={backgroundImage.src}
          alt={backgroundImage.alt || "Fondo"}
          className={`absolute top-0 left-0 w-full h-full ${backgroundImage.className || "object-cover"}`}
          zIndex={0}
          style={{
            ...backgroundImage.style
          }}
          loading={pageConfig.id === 0 ? "eager" : "lazy"}
          fetchPriority={pageConfig.id === 0 ? "high" : "auto"}
        />
      )}

      {layers.map((layer, index) => {
        if (layer.src === "/images/layers/reception/Marco_Entrada.png") {
          return null;
        }

        return (
          <ImageLayer
            key={`layer-${index}`}
            src={layer.src}
            alt={layer.alt || `Capa ${index + 1}`}
            className={`absolute ${layer.className || ""}`}
            style={{
              ...layer.style,
              zIndex: layer.zIndex || index + 1
            }}
            isMovable={layer.isMovable || false}
            movementType={layer.movementType || "float"}
            parallaxIntensity={layer.parallaxIntensity || 0}
            onClick={layer.onClick}
            onLoad={layer.onLoad}
            loading={pageConfig.id === 0 && index < 2 ? "eager" : "lazy"} // Eager load first few layers of first page if needed
            fetchPriority={pageConfig.id === 0 && index < 2 ? "high" : "auto"}
          />
        );
      })}

      {children}
    </div>
  );
};

const PageContent = ({ pageConfig, pageIndex, children }) => {
  const {
    title,
    subtitle,
    content = {},
    layout = {}
  } = pageConfig;

  const defaultLayout = {
    container: {
      position: "center",
      alignment: "center",
      maxWidth: "4xl",
      padding: "px-8",
      customClasses: "",
      customPosition: {}
    },
    title: {
      show: true,
      size: "6xl md:text-8xl",
      weight: "font-bold",
      margin: "mb-6",
      color: "text-white",
      animation: pageIndex === 0 ? "animate-parallax-zoom" : "",
      customClasses: "",
      parallax: pageIndex === 0 ? { enabled: true, speed: -0.2 } : { enabled: false }
    },
    subtitle: {
      show: true,
      size: "xl md:text-2xl",
      weight: "font-normal",
      margin: "mb-8",
      color: "text-white",
      opacity: "opacity-80",
      animation: pageIndex === 0 ? "animate-parallax-zoom" : "",
      customClasses: "",
      parallax: pageIndex === 0 ? { enabled: true, speed: -0.1 } : { enabled: false }
    },
    pageNumber: {
      show: true,
      position: "bottom",
      size: "text-4xl",
      weight: "font-bold",
      margin: "mb-8",
      color: "text-white",
      customClasses: ""
    },
    extraContent: {
      show: false,
      content: "",
      position: "after-subtitle",
      customClasses: "",
      margin: "mb-4"
    }
  };

  const finalLayout = {
    container: { ...defaultLayout.container, ...layout.container },
    title: { ...defaultLayout.title, ...layout.title },
    subtitle: { ...defaultLayout.subtitle, ...layout.subtitle },
    pageNumber: { ...defaultLayout.pageNumber, ...layout.pageNumber },
    extraContent: { ...defaultLayout.extraContent, ...layout.extraContent }
  };

  const getContainerClasses = () => {
    const { container } = finalLayout;
    const maxWidthClass = container.maxWidth === "none" ? "w-full max-w-none" : `relative max-w-${container.maxWidth} ${container.padding} ${container.customClasses}`;
    let classes = maxWidthClass;

    if (container.alignment === "left") {
      classes += " text-left";
    } else if (container.alignment === "right") {
      classes += " text-right";
    } else {
      classes += " text-center mx-auto";
    }

    switch (container.position) {
      case "top":
        classes += " absolute top-16 left-0 right-0";
        break;
      case "bottom":
        classes += " absolute bottom-16 left-0 right-0";
        break;
      case "custom":
        classes += " absolute";
        break;
      case "center":
      default:
        if (pageIndex === 0 || pageIndex === 1) {
          classes += " absolute top-1/4 left-0 right-0";
        }
        break;
    }

    classes += " isolate z-30";
    return classes;
  };

  const getContainerStyles = () => {
    const { container } = finalLayout;
    if (container.position === "custom" && container.customPosition) {
      return {
        ...container.customPosition,
        zIndex: 30
      };
    }
    return { zIndex: 30 };
  };

  const renderTitle = () => {
    if (!finalLayout.title.show || !title) return null;
    const isRoomOrEntry = [21, 22, 23, 24, 25, 26, 31, 32, 33, 34, 35, 36].includes(pageConfig.id);
    const isHallway = [2, 3].includes(pageConfig.id);

    if (isRoomOrEntry) {
      const isEntry = [21, 23, 25, 31, 33, 35].includes(pageConfig.id);
      return (
        <h1
          className={`font-room-title ${isEntry ? 'text-sm sm:text-base md:text-lg font-semibold text-white/95' : 'text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-white'
            } tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] whitespace-nowrap overflow-hidden text-ellipsis max-w-[70vw] md:max-w-none select-none`}
        >
          {title}
        </h1>
      );
    }

    const titleClasses = `text-${finalLayout.title.size} ${finalLayout.title.weight} ${finalLayout.title.margin || ""} ${finalLayout.title.color} ${finalLayout.title.customClasses || ""} leading-tight ${pageIndex === 0 ? 'animate-dramatic-title' : ''
      }`;

    if (isHallway) {
      const alignClass = finalLayout.container.alignment === "left"
        ? "ml-4 sm:ml-8 md:ml-12"
        : finalLayout.container.alignment === "right"
          ? "mr-4 sm:mr-8 md:mr-12"
          : "mx-auto";

      return (
        <div className={`inline-flex flex-col items-center px-5 py-2.5 rounded-2xl bg-white/75 dark:bg-gray-900/75 backdrop-blur-md border border-white/40 dark:border-white/15 shadow-xl shadow-black/5 ${alignClass}`}>
          <h1 className={titleClasses}>{title}</h1>
          {finalLayout.subtitle.show && subtitle && (
            <p className={`text-${finalLayout.subtitle.size} ${finalLayout.subtitle.weight} ${finalLayout.subtitle.color} ${finalLayout.subtitle.opacity || "opacity-80"} mt-0.5`}>
              {subtitle}
            </p>
          )}
        </div>
      );
    }

    return <h1 className={titleClasses}>{title}</h1>;
  };

  const renderSubtitle = () => {
    if (!finalLayout.subtitle.show || !subtitle) return null;
    if ([2, 3].includes(pageConfig.id)) return null; // Rendered together in badge for hallways
    const subtitleClasses = `text-${finalLayout.subtitle.size} ${finalLayout.subtitle.weight} ${finalLayout.subtitle.margin} ${finalLayout.subtitle.color} ${finalLayout.subtitle.opacity} ${finalLayout.subtitle.customClasses} ${pageIndex === 0 ? 'animate-dramatic-subtitle' : ''
      }`;
    return <p className={subtitleClasses}>{subtitle}</p>;
  };

  const renderPageNumber = () => {
    if (!finalLayout.pageNumber.show) return null;
    return (
      <div className={`text-center ${finalLayout.pageNumber.margin} ${finalLayout.pageNumber.customClasses}`}>
        <div className={`${finalLayout.pageNumber.size} ${finalLayout.pageNumber.weight} ${finalLayout.pageNumber.color} mb-2`}>
        </div>
      </div>
    );
  };

  const renderExtraContent = () => {
    if (!finalLayout.extraContent.show || !finalLayout.extraContent.content) return null;
    return (
      <div className={`${finalLayout.extraContent.margin} ${finalLayout.extraContent.customClasses}`}
        dangerouslySetInnerHTML={{ __html: finalLayout.extraContent.content }} />
    );
  };

  const renderContent = () => {
    const elements = [];
    if (finalLayout.extraContent.position === "before-title") elements.push(<div key="extra-before">{renderExtraContent()}</div>);
    elements.push(<div key="title">{renderTitle()}</div>);
    if (finalLayout.extraContent.position === "after-title") elements.push(<div key="extra-after">{renderExtraContent()}</div>);
    elements.push(<div key="subtitle">{renderSubtitle()}</div>);
    if (finalLayout.extraContent.position === "after-subtitle") elements.push(<div key="extra-after-sub">{renderExtraContent()}</div>);
    if (finalLayout.pageNumber.position === "top") elements.unshift(<div key="page-top">{renderPageNumber()}</div>);
    if (finalLayout.pageNumber.position === "bottom") elements.push(<div key="page-bottom">{renderPageNumber()}</div>);
    return elements;
  };

  return (
    <div className={getContainerClasses()} style={getContainerStyles()}>
      {renderContent()}
      {children}
    </div>
  );
};

const HomePage = () => {
  const {
    widgetVisible,
    widgetPosition,
    widgetSize,
    isDragging,
    isResizing,
    dragOffset,
    widgetLoaded,
    widgetContainerRef,
    setWidgetVisible,
    setWidgetPosition,
    setWidgetSize,
    setIsDragging,
    setIsResizing,
    setDragOffset,
    initializeWidget,
    reloadWidgetSilent,
    sendIntent,
    sendCustomAction,
    handleDragStart,
    handleResizeStart,
    updateWidgetTitle
  } = useChatWidget();

  const { slug } = useParams();
  const vfNavRef = useRef(false); // Flag sincrónico: navegación originada por VF
  const vfRoomRef = useRef(null); // Sala (pageId) que Voiceflow tiene cargada; null = recepción

  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [targetIndex, setTargetIndex] = useState(null);
  const [transitionDuration, setTransitionDuration] = useState(1000);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [resolution, setResolution] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });

  const deviceProfile = useMemo(() => {
    return getExtendedDeviceProfile(resolution.width, resolution.height);
  }, [resolution.width, resolution.height]);

  const deviceKey = deviceProfile.legacyKey;
  const isMobile = deviceProfile.isMobile;
  const isTablet = deviceProfile.isTablet;
  const isLandscape = deviceProfile.isLandscape;
  const isMac = deviceProfile.deviceCategory === 'laptop_mac';
  const isSurfaceDuo = deviceKey.startsWith('surfaceDuo');
  const isZFold = deviceKey.startsWith('zFold');

  // Helper para crear configs rápidas con solo los valores que cambian
  const dev = (overrides) => ({
    default: 1,
    ...overrides
  });

  // Obtiene un valor exacto basado en el deviceKey actual
  const getByDevice = (config) => {
    if (!config) return undefined;
    return config[deviceKey] !== undefined ? config[deviceKey] : config.default;
  };

  // Obtiene valores con fallback de jerarquía
  const getDevPos = (config) => {
    if (!config) return deviceKey;

    let result = config.default ? { ...config.default } : {};

    let tierConfig = null;
    if (isMobile && config.mobile) {
      tierConfig = config.mobile;
    } else if (isTablet && (config.ipad || config.tablet)) {
      tierConfig = config.ipad || config.tablet;
    } else if (!isMobile && !isTablet) {
      tierConfig = config.desktop || config.mac || config.fhd;
    }

    if (tierConfig && typeof tierConfig === 'object') {
      if (tierConfig.right !== undefined && tierConfig.left === undefined) delete result.left;
      if (tierConfig.left !== undefined && tierConfig.right === undefined) delete result.right;
      if (tierConfig.top !== undefined && tierConfig.bottom === undefined) delete result.bottom;
      if (tierConfig.bottom !== undefined && tierConfig.top === undefined) delete result.top;
      result = { ...result, ...tierConfig };
    } else if (tierConfig !== null && tierConfig !== undefined) {
      result = tierConfig;
    }

    const specific = config[deviceKey];
    if (specific !== undefined) {
      if (typeof specific === 'object' && typeof result === 'object') {
        if (specific.right !== undefined && specific.left === undefined) delete result.left;
        if (specific.left !== undefined && specific.right === undefined) delete result.right;
        if (specific.top !== undefined && specific.bottom === undefined) delete result.bottom;
        if (specific.bottom !== undefined && specific.top === undefined) delete result.top;
        result = { ...result, ...specific };
      } else {
        result = specific;
      }
    }

    return result;
  };

  // Debug en consola
  //console.log(`📱 ${resolution.width}x${resolution.height} | Mobile: ${isMobile} | Landscape: ${isLandscape} | Device: ${deviceKey}`);

  // Listener para actualizar la resolución en tiempo real y cambios de orientación
  useEffect(() => {
    const handleResize = () => {
      setResolution({
        width: window.innerWidth,
        height: window.innerHeight
      });
      setLastUpdate(Date.now());
    };

    // Llamada inicial para asegurar que tenemos valores correctos desde el principio
    handleResize();

    window.addEventListener('resize', handleResize);

    // Al rotar, algunos navegadores tardan unos ms en actualizar las dimensiones finales
    const handleOrientation = () => {
      handleResize();
      setTimeout(handleResize, 100);
      setTimeout(handleResize, 300);
    };

    window.addEventListener('orientationchange', handleOrientation);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, []);

  const pages = useMemo(() => [
    //Página 0 - Recepción lejana
    {
      id: 0,
      title: "Bienvenid@ a nuestra Consulta Online",
      subtitle: "Unidad Especializada en Tratamiento de TDAH",
      bgColor: "from-blue-900 via-indigo-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 3000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "center",
          customPosition: {
            top: isMobile ? "10%" : "35%",
            left: isMobile ? 0 : "50px",
            right: 0,
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: "4xl md:text-4xl",
          weight: "font-bold",
          margin: "mb-6",
          color: "text-white",
          animation: "animate-parallax-zoom animate-door-entry",
          parallax: { enabled: true, speed: -0.2 }
        },
        subtitle: {
          show: true,
          size: "2xl md:text-3xl",
          margin: "mb-6",
          color: "text-white",
          opacity: "opacity-80",
          animation: "animate-parallax-zoom animate-door-entry-delayed",
          parallax: { enabled: false, speed: -0.1 }
        },
        pageNumber: {
          show: false,
          position: "bottom"
        }
      },
      extraContent: {
        show: true
      },
      backgroundImage: {
        src: "/images/backgrounds/0_Recepcion_lejos.jpg",
        alt: "Fondo de bienvenida"
      },
      path: "/"
    },
    //Página 1 - Recepción cerca
    {
      id: 1,
      title: "",
      subtitle: "",
      bgColor: "from-blue-900 via-cyan-900 to-teal-900",
      autoAdvance: false,
      autoAdvanceTime: 2000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(1, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "center",
          customPosition: {
            top: isMobile ? "10%" : "8%",
            left: 0,
            right: 0,
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: "4xl md:text-4xl",
          color: "text-white"
        },
        subtitle: {
          show: true,
          size: "4xl md:text-4xl",
          color: "text-white",
          opacity: "opacity-90"
        },
        pageNumber: {
          show: false,
          position: "bottom",
          color: "text-cyan-300"
        },
        extraContent: {
          show: false,
          content: `
            <div class="mt-8 space-y-4">
              <div class="text-lg opacity-70 text-cyan-200">
                Chat Clínica Milos activado automáticamente
              </div>
              <div class="flex justify-center space-x-2 flex-wrap gap-2">
                <button id="toggle-chat" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold transition-colors text-sm">
                  Mostrar Chat
                </button>
                <button id="send-message" class="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold transition-colors text-sm">
                  Enviar Mensaje
                </button>
                <button id="center-iframe" class="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white font-semibold transition-colors text-sm">
                  Centrar
                </button>
              </div>
              <div class="text-sm opacity-60 text-green-300">
                Chat Clínica Milos listo para usar
              </div>
            </div>
            <button 
              onClick={() => {
                const event = new CustomEvent('voiceflow-navigate', {
                  detail: { page: 3 }
                });
                window.dispatchEvent(event);
              }}
              className="fixed bottom-20 left-6 z-50 px-4 py-2 bg-green-500 text-white rounded"
            >
              Test Navigate to Page 3
            </button>
          `,
          position: "bottom"
        }
      },
      backgroundImage: {
        src: (isTablet && !isMac) ? "/images/backgrounds/0_Recepcion_lejos.jpg" : "/images/backgrounds/1_Recepcion_cerca.jpg",
        alt: "Recepción de Clínica Milos",
        className: "object-cover"
      },
      path: "/reception",
    },
    //Página 2 - Pasillo Unidad de Psicología
    {
      id: 2,
      title: "Pasillo Unidad de Psicología",
      subtitle: "Selecciona una sala para continuar",
      bgColor: "from-orange-900 via-red-900 to-pink-900",
      autoAdvance: false,
      autoAdvanceTime: 1000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "right",
          maxWidth: "none",
          customPosition: {
            top: isMobile ? "4.5rem" : "5rem",
            left: 0,
            right: 0,
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          margin: "mb-0"
        },
        subtitle: {
          show: true,
          size: isMobile ? "xs" : "xs md:text-sm",
          color: "text-gray-600 dark:text-gray-300 font-medium"
        },
        pageNumber: {
          show: false
        },
      },
      backgroundImage: {
        src: getDevPos({
          zenbookFold: "/images/backgrounds/2_hallway_east_mobile.jpg",
          zenbookFoldLand: "/images/backgrounds/2_hallway_east.jpg",
          mobile: "/images/backgrounds/2_hallway_east_mobile.jpg",
          mobileLandscape: "/images/backgrounds/2_hallway_east.jpg",
          seLand: "/images/backgrounds/2_hallway_east.jpg",
          xrLand: "/images/backgrounds/2_hallway_east.jpg",
          i12Land: "/images/backgrounds/2_hallway_east.jpg",
          i14ProLand: "/images/backgrounds/2_hallway_east.jpg",
          s8Land: "/images/backgrounds/2_hallway_east.jpg",
          pixel7Land: "/images/backgrounds/2_hallway_east.jpg",
          ipad: isLandscape ? "/images/backgrounds/2_hallway_east.jpg" : "/images/backgrounds/2_hallway_east_mobile.jpg",
          mac: "/images/backgrounds/2_hallway_east.jpg",
          surfaceDuoLand: "/images/backgrounds/2_hallway_east.jpg",
          zFold: "/images/backgrounds/2_hallway_east_mobile.jpg",
          galaxyA51Land: "/images/backgrounds/2_hallway_east.jpg",
          default: "/images/backgrounds/2_hallway_east.jpg"
        }),
        alt: "Pasillo Unidad de Psicología",
        className: "object-cover"
      },
      path: "/hallway_east"
    },
    //Página 3 - Pasillo Unidad TDAH
    {
      id: 3,
      title: "Pasillo Unidad TDAH",
      subtitle: "Selecciona una sala para continuar",
      bgColor: "from-orange-900 via-red-900 to-pink-900",
      autoAdvance: false,
      autoAdvanceTime: 1000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          maxWidth: "none",
          customPosition: {
            top: isMobile ? "4.5rem" : "5rem",
            left: 0,
            right: 0,
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          margin: "mb-0"
        },
        subtitle: {
          show: true,
          size: isMobile ? "xs" : "xs md:text-sm",
          color: "text-gray-600 dark:text-gray-300 font-medium"
        },
        pageNumber: {
          show: false
        },
      },
      backgroundImage: {
        src: getDevPos({
          zenbookFold: "/images/backgrounds/3_hallway_west_mobile.jpg",
          zenbookFoldLand: "/images/backgrounds/3_hallway_west.jpg",
          mobile: "/images/backgrounds/3_hallway_west_mobile.jpg",
          mobileLandscape: "/images/backgrounds/3_hallway_west.jpg",
          seLand: "/images/backgrounds/3_hallway_west.jpg",
          xrLand: "/images/backgrounds/3_hallway_west.jpg",
          i12Land: "/images/backgrounds/3_hallway_west.jpg",
          i14ProLand: "/images/backgrounds/3_hallway_west.jpg",
          pixel7Land: "/images/backgrounds/3_hallway_west.jpg",
          s8Land: "/images/backgrounds/3_hallway_west.jpg",
          ipad: isLandscape ? "/images/backgrounds/3_hallway_west.jpg" : "/images/backgrounds/3_hallway_west_mobile.jpg",
          mac: "/images/backgrounds/3_hallway_west.jpg",
          surfaceDuoLand: "/images/backgrounds/3_hallway_west.jpg",
          zFold: "/images/backgrounds/3_hallway_west_mobile.jpg",
          galaxyA51Land: "/images/backgrounds/3_hallway_west.jpg",
          default: "/images/backgrounds/3_hallway_west.jpg"
        }),
        alt: "Pasillo Unidad TDAH",
        className: getByDevice({
          zenbookFold: "object-contain",
          mobile: "object-contain",
          zFold: "object-cover",
          ipad: "object-cover",
          default: "object-cover"
        })
      },
      path: "/hallway_west"
    },
    //Página 21 - Entrando a sala de tratamiento de Ansiedad y Depresión (Llegando desde Página 2)
    {
      id: 21,
      title: "Entrando a sala de tratamiento de Ansiedad y Depresión",
      subtitle: "",
      bgColor: "from-red-900 via-pink-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/21_room_01.jpg",
        alt: "Entrada a sala de terapia de ansiedad y depresión"
      },
      path: "/psico01-office-entry"
    },
    //Página 22 - Sala de terapia de Ansiedad y Depresión (Llegando desde página 21)
    {
      id: 22,
      title: isMobile ? "Ansiedad y Depresión" : "Terapia de Ansiedad y Depresión",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(22, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/22_room_01.jpg",
        alt: "Interior de sala de terapia de ansiedad y depresión"
      },
      path: "/psico01-office",
    },
    //Página 23 - Entrando a Sala de terapia de Parejas (Llegando desde página 2)
    {
      id: 23,
      title: "Entrando a sala de terapia de Parejas",
      subtitle: "",
      bgColor: "from-red-900 via-pink-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/23_room_02.jpg",
        alt: "Entrada a sala de terapia de parejas"
      },
      path: "/psico02-office-entry"
    },
    //Página 24 - Sala de terapia de Parejas (Llegando desde página 23)
    {
      id: 24,
      title: "Terapia de Parejas",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(24, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/24_room_02.jpg",
        alt: "Interior de sala de terapia de parejas"
      },
      path: "/psico02-office",
    },
    //Página 25 - Entrando a Sala de Crecimiento Personal (Llegando desde página 2)
    {
      id: 25,
      title: "Entrando a sala de Crecimiento Personal",
      subtitle: "",
      bgColor: "from-red-900 via-pink-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/25_room_03.jpg",
        alt: "Entrada a sala de crecimiento personal"
      },
      path: "/psico03-office-entry"
    },
    //Página 26 - Sala de Crecimiento Personal (Llegando desde página 25)
    {
      id: 26,
      title: "Crecimiento Personal",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(26, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/26_room_03.jpg",
        alt: "Interior de sala de crecimiento personal"
      },
      path: "/psico03-office",
    },
    //Página 31 - Entrando a sala Evaluación/Test TDAH (Llegando desde Página 3)
    {
      id: 31,
      title: "Entrando a Evaluación/Test TDAH",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/31_room_01.jpg",
        alt: "Interior de sala de evaluación/test TDAH"
      },
      path: "/tdah01-office-entry"
    },
    //Página 32 - Evaluación/Test TDAH (Llegando desde página 31)
    {
      id: 32,
      title: "Evaluación/Test TDAH",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(32, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/32_room_01.jpg",
        alt: "Interior de sala de evaluación/test TDAH"
      },
      path: "/tdah01-office",
    },
    //Página 33 - Entrando a sala de Terapia de TDAH para Adultos (Llegando desde Página 3)
    {
      id: 33,
      title: "Entrando a terapia de TDAH para Adultos",
      subtitle: "",
      bgColor: "from-red-900 via-pink-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/33_room_02.jpg",
        alt: "Entrada a sala de terapia de TDAH para adultos"
      },
      path: "/tdah02-office-entry"
    },
    //Página 34 - Terapia de TDAH para Adultos (Llegando desde página 33)
    {
      id: 34,
      title: "TDAH para Adultos",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(34, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/34_room_02.jpg",
        alt: "Interior de la sala de terapia de TDAH para adultos"
      },
      path: "/tdah02-office",
    },
    //Página 35 - Entrando a sala de Asesoramiento de familias TDAH (Llegando desde Página 3)
    {
      id: 35,
      title: "Entrando a sala de Asesoramiento de familias TDAH",
      subtitle: "",
      bgColor: "from-red-900 via-pink-900 to-purple-900",
      autoAdvance: true,
      autoAdvanceTime: 2000,
      autoOpenWidget: false,
      widgetAlignment: 0,
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "xs sm:text-sm" : "xs md:text-sm lg:text-base",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[80vw] md:max-w-none"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/35_room_03.jpg",
        alt: "Entrada a sala de asesoramiento de familias TDAH"
      },
      path: "/tdah03-office-entry"
    },
    //Página 36 - Asesoramiento de familias TDAH (Llegando desde página 35)
    {
      id: 36,
      title: "Asesoramiento de familias TDAH",
      subtitle: "",
      bgColor: "from-slate-900 via-gray-900 to-zinc-900",
      autoAdvance: false,
      autoAdvanceTime: 5000,
      autoOpenWidget: true,
      widgetAlignment: 0,
      chatConfig: getWidgetConfig(36, resolution, isLandscape),
      layout: {
        container: {
          position: "custom",
          alignment: "left",
          customPosition: {
            top: isMobile ? "4.75rem" : "5.25rem",
            left: isMobile ? "1rem" : "1.75rem",
            position: "absolute"
          }
        },
        title: {
          show: true,
          size: isMobile ? "sm sm:text-base" : "base md:text-lg lg:text-xl",
          color: "text-gray-900 dark:text-white font-bold",
          customClasses: "tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        },
        subtitle: {
          show: false
        },
        pageNumber: {
          show: false
        }
      },
      backgroundImage: {
        src: "/images/backgrounds/36_room_03.jpg",
        alt: "Interior de sala de asesoramiento de familias TDAH"
      },
      path: "/tdah03-office",
    },
  ], [resolution, isMobile, isTablet, isLandscape, deviceKey, isMac, isSurfaceDuo, isZFold]);

  // === ACCESO DIRECTO A SALA POR URL (/sala/:slug) ==========================
  //
  // Se entra por la página de ENTRADA de la sala (no por la de servicio): su
  // autoAdvance encadena hasta el despacho y así se conserva la cinemática.
  //
  // Importante: NO se usa el evento 'voiceflow-navigate'. Ese evento marca la
  // navegación como originada por Voiceflow (vfNavRef) y suprimiría el intent
  // pageNavigationInfo, dejando al agente en el contexto de recepción. Al
  // cambiar currentPage directamente, el efecto de sincronización se encarga
  // de avisar a Voiceflow como en cualquier navegación del usuario.
  const roomFromUrl = getRoomBySlug(slug);
  const deepLinkAppliedRef = useRef(null);

  useLayoutEffect(() => {
    if (!roomFromUrl) return;
    // Sólo una vez por slug: si no, cualquier re-render devolvería al usuario
    // a la página de entrada mientras navega por dentro del recorrido
    if (deepLinkAppliedRef.current === slug) return;
    deepLinkAppliedRef.current = slug;

    const targetIdx = pages.findIndex(p => p.id === roomFromUrl.entryPage);
    if (targetIdx < 0) return;

    console.log('[Sala-URL] Entrada directa a la sala:', slug, '→ página', roomFromUrl.entryPage);
    // useLayoutEffect: se aplica antes del primer pintado, sin fogonazo de recepción
    setCurrentPage(targetIdx);
    setIsTransitioning(false);
    setTargetIndex(null);
    setTransitionProgress(0);
  }, [slug, roomFromUrl, pages]);

  // Control Maestro del Chat mediante getDevPos
  useEffect(() => {
    if (!widgetLoaded) return;

    // Si la página actual tiene su propia configuración, la usamos; si no, una por defecto.
    const chatParams = pages[currentPage]?.chatConfig || {
      default: { width: 450, height: 600, right: 40, top: 100 },
      fhd: { width: 450, height: 600, right: 60, top: (resolution.height - 600) / 2.3 },
      mac: { width: 400, height: 600, right: 40, bottom: 40 },
      ipad: { width: 450, height: 600, left: 'center', bottom: 20 },
      mobile: {
        width: Math.round(resolution.width * 0.9),
        height: isLandscape ? Math.round(resolution.height * 0.5) : Math.round(resolution.height * 0.45),
        left: 'center',
        bottom: 10
      }
    };

    const chatConfig = getDevPos(chatParams);

    const width = Math.round(chatConfig.width || 400);
    const height = Math.round(chatConfig.height || 600);

    let x = 0;
    let y = 0;

    const parseChatDim = (val, total) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && val.endsWith('%')) {
        return Math.round((parseFloat(val) / 100) * total);
      }
      return parseFloat(val) || 0;
    };

    // Lógica para X (Left gana a Right)
    if (chatConfig.left === 'center') {
      x = Math.round((resolution.width - width) / 2);
    } else if (chatConfig.left !== undefined) {
      x = parseChatDim(chatConfig.left, resolution.width);
    } else if (chatConfig.right !== undefined) {
      x = Math.round(resolution.width - width - parseChatDim(chatConfig.right, resolution.width));
    }

    // Lógica para Y (Top gana a Bottom)
    if (chatConfig.top === 'center') {
      y = Math.round((resolution.height - height) / 2);
    } else if (chatConfig.top !== undefined) {
      y = parseChatDim(chatConfig.top, resolution.height);
    } else if (chatConfig.bottom !== undefined) {
      y = Math.round(resolution.height - height - parseChatDim(chatConfig.bottom, resolution.height));
    }

    setWidgetSize({ width, height });
    setWidgetPosition({ x, y });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution.width, resolution.height, widgetLoaded, currentPage, JSON.stringify(pages[currentPage]?.chatConfig), isLandscape]);

  // Actualización dinámica del título del chat según la página / sala
  useEffect(() => {
    if (!widgetLoaded) return;
    const pageId = pages[currentPage]?.id;
    const newTitle = getWidgetTitle(pageId);
    updateWidgetTitle(newTitle);
  }, [currentPage, widgetLoaded, updateWidgetTitle, pages]);

  useEffect(() => {
    const preloadImages = [
      "/images/backgrounds/1_Recepcion_cerca.webp",
      "/images/backgrounds/2_Pasillo de acceso a despachos.webp"
    ];
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Recalcula la posición del widget al cambiar tamaño de ventana (ELIMINADO: GESTIONADO POR getDevPos)

  // Recalcula la posición del widget al cambiar tamaño de ventana (ELIMINADO: GESTIONADO POR getDevPos)


  // Páginas permitidas para enviar información a Voiceflow
  // Solo páginas de entrada/transición: 21, 23, 25, 31, 33, 35
  // Memoizar para evitar recalcular en cada render
  // Las salas que se anuncian a Voiceflow (21/23/25 = ala este, 31/33/35 = ala
  // oeste) y su correspondencia con cada página están en config/rooms.js
  // (VF_ROOM_BY_PAGE), compartidas con el enrutado de URLs /sala/:slug.

  // ¿Están activados los "Listen for other triggers" en Voiceflow?
  //
  // Hoy están DESACTIVADOS en los nodos donde la conversación se queda parada
  // dentro de cada sala ("Gestión intención Agente TDAH01/02/03 y ONLINE01/02/03",
  // los pasos de agente de cada sala y "Agente respuesta - Recepcion"). Con ese
  // toggle en off el runtime descarta el intent pageNavigationInfo que envía la
  // web al cambiar de sala:
  //   Trigger request is ignored - "Listen for other triggers" is disabled.
  // ...y se lo entrega al agente LLM, por lo que la sala nueva nunca carga.
  // Mientras siga así hay que reiniciar la sesión antes de pedir la sala nueva.
  //
  // Al activar esos toggles en Voiceflow → poner true: se elimina el reinicio,
  // el cambio de sala es inmediato y no se pierde el historial del chat.
  // VERIFICADO 2026-08-24 en el proyecto de producción: siguen DESACTIVADOS.
  // Se comprobó en navegador que, con este flag en true, la web envía el intent
  // pageNavigationInfo pero Voiceflow lo descarta y el agente responde con la
  // bienvenida de recepción en lugar de la sala pedida.
  // Cuando se activen los toggles en Voiceflow → poner true (evita reiniciar la
  // sesión y conserva el historial del chat al cambiar de sala).
  const VF_LISTEN_OTHER_TRIGGERS = false;

  // Sincronización de la sala de Voiceflow con la página de la web
  useEffect(() => {
    const pageId = pages[currentPage]?.id;
    if (isTransitioning || !widgetLoaded) return;

    // Sala de Voiceflow a la que pertenece la página actual. Página de entrada y
    // página de servicio comparten sala (identificada por el id de la entrada),
    // así que el intent se envía igual si el widget no llegó a cargar durante la
    // entrada — el caso de una URL directa /sala/:slug abierta en frío.
    const vfRoom = VF_ROOM_BY_PAGE[pageId];
    const isRoom = vfRoom !== undefined;
    const isReception = pageId === 1;
    if (!isRoom && !isReception) return;
    if (isRoom && vfRoomRef.current === vfRoom) return;
    // Recepción: sólo hay que reencaminar Voiceflow si venimos de una sala
    // (en el arranque vfRoomRef es null y el widget ya lanza su bienvenida)
    if (isReception && vfRoomRef.current === null) return;

    // Navegación ordenada por el propio Voiceflow (carrusel de especialistas):
    // VF ya está dentro de la sala, no hay que pedirle nada
    if (vfNavRef.current) {
      console.log('[VF-Nav] Navegación originada por Voiceflow, no se envía intent');
      vfNavRef.current = false;
      vfRoomRef.current = isRoom ? vfRoom : null;
      return;
    }

    const pageIntent = (targetId) => ({
      type: 'intent',
      payload: {
        query: `Navegar a pagina ${targetId}`,
        intent: { name: 'pageNavigationInfo' },
        entities: [{ name: 'page', value: String(targetId) }]
      }
    });

    const sendPageIntent = (targetId, retry = true) => {
      console.log('[VF-Nav] Enviando pageNavigationInfo a Voiceflow:', targetId);
      const intent = pageIntent(targetId);
      const sent = sendIntent(intent);
      // Si el widget aún no tenía sesión, la promesa se rechaza y el intent se
      // pierde (la sala se quedaría en blanco): un único reintento lo cubre
      if (retry && sent && typeof sent.catch === 'function') {
        sent.catch(() => setTimeout(() => sendPageIntent(targetId, false), 800));
      }
      vfRoomRef.current = isRoom ? targetId : null;
    };

    if (VF_LISTEN_OTHER_TRIGGERS) {
      sendPageIntent(isReception ? 1 : vfRoom);
      return;
    }

    // Sin triggers globales hay que reiniciar la sesión, y el reinicio no puede
    // pasar por el launch normal: el widget arranca siempre la conversación al
    // montarse y su launch por defecto ejecuta Start → bloque "Bienvenida", que
    // es el saludo que se colaba delante del especialista nuevo.
    // Recreando el widget con launch.event = pageNavigationInfo, ese intent es
    // el PRIMER (y único) request de la sesión: engancha con el comando global
    // ya en Start y entra directo en la sala.
    console.log('[VF-Nav] Recreando widget con apertura directa en sala:', vfRoom);
    // Se marca el destino ANTES de recargar: así un re-render mientras el
    // widget se reconstruye no vuelve a entrar aquí y encadena recargas
    const target = isReception ? 1 : vfRoom;
    vfRoomRef.current = isRoom ? vfRoom : null;
    reloadWidgetSilent(pageIntent(target));
  }, [currentPage, isTransitioning, widgetLoaded, sendIntent, reloadWidgetSilent, pages]);

  // Animación de transición entre páginas
  useEffect(() => {
    if (isTransitioning) {
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const prog = Math.min(elapsed / transitionDuration, 1);
        setTransitionProgress(prog);
        if (prog < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isTransitioning, transitionDuration]);

  // Lógica de auto-avance de página
  useEffect(() => {
    const currentPageConfig = pages[currentPage];
    if (currentPageConfig.autoAdvance && currentPage < pages.length - 1) {
      const timer = setTimeout(() => {
        goToPage(pages[currentPage + 1].id);
      }, currentPageConfig.autoAdvanceTime);
      return () => clearTimeout(timer);
    }
  }, [currentPage, pages]);

  // Lógica de apertura automática del widget al cargar página (Restaurada)
  useEffect(() => {
    const config = pages[currentPage];
    if (config?.autoOpenWidget && !isTransitioning) {
      if (!widgetLoaded) {
        setWidgetVisible(true);
        const timer = setTimeout(() => initializeWidget(), 500);
        return () => clearTimeout(timer);
      } else {
        setWidgetVisible(true);
      }
    }
  }, [currentPage, isTransitioning, widgetLoaded]);

  // Fallback de seguridad para evitar errores de referencia residuales
  const getResponsiveWidgetPosition = () => {
    console.warn("Llamada a función obsoleta detectada. Usando sistema getDevPos.");
    return { x: 0, y: 0 }; // El useEffect maestro sobreescribirá esto instantáneamente
  };

  // Control de navegación desde Voiceflow — ignora los primeros 3s (carga inicial VF)
  const vfReadyRef = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => { vfReadyRef.current = true; }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleVoiceflowNavigation = (event) => {
      const targetPageId = parseInt(event.detail.page);
      // Sin 'source' se asume Voiceflow: es quien despacha este evento
      const origen = event.detail.source || 'voiceflow';
      const targetPageIndex = pages.findIndex(page => page.id === targetPageId);

      if (!vfReadyRef.current) {
        console.log('[VF-Nav] Ignorando navegación durante carga inicial:', targetPageId);
        return;
      }

      if (isNaN(targetPageIndex) || targetPageIndex < 0 || targetPageIndex >= pages.length) {
        console.warn('[VF-Nav] Página inválida:', event.detail.page);
        return;
      }

      // Usar callback de setState para leer currentPage actualizado (sin closure stale)
      setCurrentPage(prev => {
        if (prev === targetPageIndex) {
          console.log('[VF-Nav] Ya estamos en la página', targetPageId, '- ignorando');
          return prev;
        }
        console.log('[VF-Nav] Navegando a página:', targetPageId, '(origen:', origen + ')');
        // Sólo se suprime el intent si la navegación la ordenó Voiceflow: el
        // agente ya está en la sala. Si la originó el usuario hay que avisarle,
        // o el chat se quedaría hablando desde el contexto anterior.
        vfNavRef.current = (origen === 'voiceflow'); // Flag sincrónico ANTES del cambio de página
        setWidgetVisible(false);
        setTargetIndex(targetPageIndex);
        setTransitionDuration(800);
        setTransitionProgress(0);
        setIsTransitioning(true);
        // Completar transición (mismo flujo que goToPage)
        setTimeout(() => {
          setCurrentPage(targetPageIndex);
          setIsTransitioning(false);
          setTargetIndex(null);
          setTransitionProgress(0);

          const config = pages[targetPageIndex];
          if (config.autoOpenWidget) {
            setWidgetVisible(true);
          }
        }, 800);
        return prev; // No cambiar aún, el setTimeout lo hará
      });
    };
    window.addEventListener('voiceflow-navigate', handleVoiceflowNavigation);
    return () => window.removeEventListener('voiceflow-navigate', handleVoiceflowNavigation);
  }, [pages]);
  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle')) return;
    setIsDragging(true);
    const rect = widgetContainerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Control del redimensionamiento y arrastre del widget usando el ratón
  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - widgetSize.width;
      const maxY = window.innerHeight - widgetSize.height;
      setWidgetPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
    if (isResizing) {
      const newWidth = Math.max(300, e.clientX - widgetPosition.x);
      const newHeight = Math.max(300, e.clientY - widgetPosition.y);
      setWidgetSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, widgetPosition, widgetSize]);

  // Mantener el widget fijo cuando scrollea el contenedor padre
  useEffect(() => {
    const scrollContainer = document.querySelector('.w-full.overflow-y-auto');
    if (!scrollContainer || !widgetContainerRef.current) return;

    const handleScroll = () => {
      if (widgetContainerRef.current && widgetVisible) {
        // Cuando el contenedor scrollea, ajustamos la posición del widget
        // para mantenerlo visualmente en el mismo lugar usando transform
        const scrollTop = scrollContainer.scrollTop;
        widgetContainerRef.current.style.transform = `translateY(${scrollTop}px)`;
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [widgetVisible, widgetPosition]);

  const getPageClasses = (pageIndex) => {
    const isActive = pageIndex === currentPage;
    const isTarget = pageIndex === targetIndex;
    const currentPageId = pages[currentPage]?.id;
    const targetPageId = targetIndex !== null ? pages[targetIndex]?.id : null;

    const isZoomIntro = (currentPageId === 0 && targetPageId === 1) || (currentPageId === 1 && targetPageId === 0);

    let base = `absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center text-white`;

    if (!isTransitioning) {
      return isActive ? `${base} opacity-100 z-20` : `${base} opacity-0 z-1 pointer-events-none`;
    }

    if (isZoomIntro) {
      if (targetPageId === 1) {
        if (isTarget) return `${base} animate-zoom-in z-30`;
        if (isActive) return `${base} animate-zoom-out z-20`;
      } else {
        if (isTarget) return `${base} animate-zoom-in-from-far z-30`;
        if (isActive) return `${base} animate-zoom-out-to-far z-20`;
      }
    }

    return `${base} opacity-100 z-25`;
  };

  const goToPage = (pageId) => {
    const pageIndex = pages.findIndex(p => p.id === pageId);
    if (pageIndex === -1 || pageIndex === currentPage || isTransitioning) return;

    const currentId = pages[currentPage].id;
    const isZoomIntro = (currentId === 0 && pageId === 1) || (currentId === 1 && pageId === 0);
    const duration = isZoomIntro ? 1400 : 900;

    setWidgetVisible(false);
    setTargetIndex(pageIndex);
    setTransitionDuration(duration);
    setTransitionProgress(0);
    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentPage(pageIndex);
      setIsTransitioning(false);
      setTargetIndex(null);
      setTransitionProgress(0);

      const config = pages[pageIndex];
      if (config?.autoOpenWidget && !widgetLoaded) {
        setWidgetVisible(true);
        setTimeout(() => initializeWidget(), 300);
      } else if (config?.autoOpenWidget) {
        setWidgetVisible(true);
      }
    }, duration);
  };

  const getTransitionStyle = (index) => {
    if (!isTransitioning) return {};

    const currentId = pages[currentPage]?.id;
    const targetId = targetIndex !== null ? pages[targetIndex]?.id : null;
    if (targetId === null) return {};

    const isTdahRoom = (id) => [31, 32, 33, 34, 35, 36].includes(id);
    const isPsicoRoom = (id) => [21, 22, 23, 24, 25, 26].includes(id);

    // 1. TDAH Corridor <-> TDAH Rooms (Pasillo TDAH moves LEFT, room enters from RIGHT)
    if (currentId === 3 && isTdahRoom(targetId)) {
      if (index === currentPage) {
        return { transform: `translateX(${-100 * transitionProgress}%)`, opacity: 1, zIndex: 20 };
      }
      if (index === targetIndex) {
        return { transform: `translateX(${100 - 100 * transitionProgress}%)`, opacity: 1, zIndex: 25 };
      }
      return { opacity: 0 };
    }
    // Reverse: Room exits to RIGHT (+100%), Pasillo TDAH enters from LEFT (-100%)
    if (isTdahRoom(currentId) && targetId === 3) {
      if (index === currentPage) {
        return { transform: `translateX(${100 * transitionProgress}%)`, opacity: 1, zIndex: 20 };
      }
      if (index === targetIndex) {
        return { transform: `translateX(${-100 + 100 * transitionProgress}%)`, opacity: 1, zIndex: 25 };
      }
      return { opacity: 0 };
    }

    // 2. Psicología Corridor <-> Psicología Rooms (Pasillo Psicología moves RIGHT, room enters from LEFT)
    if (currentId === 2 && isPsicoRoom(targetId)) {
      if (index === currentPage) {
        return { transform: `translateX(${100 * transitionProgress}%)`, opacity: 1, zIndex: 20 };
      }
      if (index === targetIndex) {
        return { transform: `translateX(${-100 + 100 * transitionProgress}%)`, opacity: 1, zIndex: 25 };
      }
      return { opacity: 0 };
    }
    // Reverse: Room exits to LEFT (-100%), Pasillo Psicología enters from RIGHT (+100%)
    if (isPsicoRoom(currentId) && targetId === 2) {
      if (index === currentPage) {
        return { transform: `translateX(${-100 * transitionProgress}%)`, opacity: 1, zIndex: 20 };
      }
      if (index === targetIndex) {
        return { transform: `translateX(${100 - 100 * transitionProgress}%)`, opacity: 1, zIndex: 25 };
      }
      return { opacity: 0 };
    }

    // 3. Reception <-> Hallways (Conveyor)
    const isConveyor = [1, 2, 3].includes(currentId) && [1, 2, 3].includes(targetId);
    if (isConveyor) {
      const getPos = (id) => {
        if (id === 3) return -1;
        if (id === 1) return 0;
        if (id === 2) return 1;
        return 0;
      };
      const currentPos = getPos(currentId);
      const targetPos = getPos(targetId);
      const goingRight = targetPos > currentPos;
      let translate = 0;
      if (index === currentPage) {
        translate = goingRight ? -100 * transitionProgress : 100 * transitionProgress;
      } else if (index === targetIndex) {
        translate = goingRight ? 100 - 100 * transitionProgress : -100 + 100 * transitionProgress;
      } else {
        return { opacity: 0 };
      }
      return {
        transform: `translateX(${translate}%)`,
        opacity: 1,
        zIndex: index === currentPage ? 20 : 25
      };
    }

    // 4. Entry to Room auto-advance (21 -> 22, 23 -> 24, etc.)
    const isEntryToRoom = (currentId === 21 && targetId === 22) ||
      (currentId === 23 && targetId === 24) ||
      (currentId === 25 && targetId === 26) ||
      (currentId === 31 && targetId === 32) ||
      (currentId === 33 && targetId === 34) ||
      (currentId === 35 && targetId === 36);
    if (isEntryToRoom) {
      if (index === currentPage) {
        return { opacity: 1 - transitionProgress, transform: `scale(${1 + 0.1 * transitionProgress})`, zIndex: 20 };
      }
      if (index === targetIndex) {
        return { opacity: transitionProgress, transform: `scale(${1.08 - 0.08 * transitionProgress})`, zIndex: 25 };
      }
      return { opacity: 0 };
    }

    return {};
  };

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Clínica Milos",
    "url": "https://tdahadultoonline.com",
    "logo": "https://tdahadultoonline.com/images/icons/Logo_Clinica_Milos_01.jpg",
    "image": "https://tdahadultoonline.com/images/icons/Logo_Clinica_Milos_01.jpg",
    "description": "Especialistas en evaluación, diagnóstico y terapia psicológica para TDAH adulto online. También atendemos ansiedad, depresión y terapia de parejas.",
    "medicalSpecialty": "Psychiatry",
    "availableService": [
      { "@type": "MedicalTherapy", "name": "Evaluación y diagnóstico TDAH adulto" },
      { "@type": "MedicalTherapy", "name": "Terapia TDAH para adultos" },
      { "@type": "MedicalTherapy", "name": "Asesoramiento familiar TDAH" },
      { "@type": "MedicalTherapy", "name": "Terapia para ansiedad y depresión" },
      { "@type": "MedicalTherapy", "name": "Terapia de parejas" },
      { "@type": "MedicalTherapy", "name": "Crecimiento personal" }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": "Spanish"
    }
  };

  return (
    <div className="w-full overflow-y-auto flex flex-col m-0 p-0">
      {roomFromUrl ? (
        <SEOHead
          title={roomFromUrl.seoTitle}
          description={roomFromUrl.seoDescription}
          keywords={roomFromUrl.seoKeywords}
          canonical={`/sala/${slug}`}
          structuredData={homeStructuredData}
        />
      ) : (
        <SEOHead
          title="Especialistas en TDAH Adulto Online"
          description="Clínica Milos: evaluación, diagnóstico y terapia para TDAH adulto online. También ansiedad, depresión y terapia de parejas. Primera consulta gratuita."
          keywords="TDAH adulto, TDAH online, evaluación TDAH, psicólogo TDAH, terapia TDAH, déficit atención adulto, TDAH diagnóstico, TDAH tratamiento, psicología online, ansiedad, depresión, terapia parejas"
          canonical="/"
          structuredData={homeStructuredData}
        />
      )}
      <div className="relative w-full h-screen overflow-hidden flex-shrink-0">
        {pages.map((page, index) => {
          const style = getTransitionStyle(index);

          return (
            <div
              key={page.id}
              className={`${getPageClasses(index)} bg-gradient-to-br ${page.bgColor}`}
              style={{
                ...style,
                backfaceVisibility: 'hidden',
                willChange: isTransitioning ? 'transform, opacity' : 'auto'
              }}
            >
              <PageLayers pageConfig={page} isActive={index === currentPage} isTransitioning={isTransitioning} currentPage={currentPage} isLandscape={isLandscape}>
                <PageContent pageConfig={page} pageIndex={index} />
              </PageLayers>
            </div>
          );
        })}


        {/* NAVIGATION BUTTONS - FIXED OUTSIDE PAGE CONTAINER */}
        {(() => {
          if (isTransitioning) return null;

          const navConfig = getNavigationConfig({ getDevPos, goToPage, isMobile, isTablet, isLandscape });

          const currentPageId = pages[currentPage]?.id;
          const config = navConfig[currentPageId];

          if (!config) return null;

          const renderButton = (btnConfig, key) => {
            if (!btnConfig) return null;

            const iconPath = {
              right: "M9 5l7 7-7 7",
              left: "M15 19l-7-7 7-7",
              down: "M19 9l-7 7-7-7"
            }[btnConfig.icon] || "M9 5l7 7-7 7";

            const getTooltipPlacement = () => {
              if (btnConfig.tooltipPlacement) return btnConfig.tooltipPlacement;
              if (btnConfig.icon === "left") return "right";
              if (btnConfig.icon === "right") return "left";
              if (btnConfig.icon === "down") return "top";
              return "top";
            };

            // Obtener tamaño de letra por resolución
            const fontSizeValue = btnConfig.fontSize ? getByDevice(btnConfig.fontSize) : "text-sm md:text-xl";

            // Separar fontSize en clase o style
            let fontSizeClass = "";
            let fontSizeStyle = {};
            if (typeof fontSizeValue === 'object' && fontSizeValue.fontSize) {
              fontSizeStyle = { fontSize: fontSizeValue.fontSize };
            } else {
              fontSizeClass = fontSizeValue;
            }

            const shouldHide = btnConfig.position?.top &&
              typeof btnConfig.position.top === 'string' &&
              btnConfig.position.top.includes('px') &&
              parseInt(btnConfig.position.top) < 80;

            if (shouldHide) return null;

            return (
              <div key={key} style={btnConfig.position} className="absolute z-50">
                <Tooltip content={btnConfig.tooltipText || btnConfig.text} placement={getTooltipPlacement()}>
                  <button
                    onClick={btnConfig.onClick}
                    style={fontSizeStyle}
                    className={`
                      p-3 md:p-4
                      bg-gray-900/5 backdrop-blur-md
                      border border-gray-900/10
                      rounded-full hover:bg-gray-900/10
                      transition-all duration-300 ease-out
                      flex items-center
                      ${btnConfig.icon === "down" ? "flex-col space-y-1 md:space-y-2" : "space-x-1 md:space-x-2"}
                      ${fontSizeClass} font-bold ${btnConfig.textColor || "text-gray-800"}
                      shadow-xl
                    `}
                  >
                    {btnConfig.icon === "left" && (
                      <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                      </svg>
                    )}
                    <span>{btnConfig.text}</span>
                    {btnConfig.icon !== "left" && (
                      <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                      </svg>
                    )}
                  </button>
                </Tooltip>
              </div>
            );
          };

          if (currentPageId === 1) {
            return (
              <>
                {renderButton(config.este, "este")}
                {renderButton(config.oeste, "oeste")}
              </>
            );
          }

          if (currentPageId === 2 || currentPageId === 3) {
            return (
              <>
                {renderButton(config.back, "back")}
                {Object.keys(config).map((k) => k !== 'back' && renderButton(config[k], k))}
              </>
            );
          }

          return renderButton(config, "single");
        })()}

        {/* BOTÓN CALENDLY - RECEPCIÓN Y TODAS LAS SALAS */}
        {(() => {
          if (isTransitioning) return null;

          const currentPageId = pages[currentPage]?.id;
          const calendlyData = CALENDLY_CONFIGS[currentPageId];
          if (!calendlyData) return null;

          const pos = getCalendlyPosition(getDevPos);
          const isMobileLandscape = isMobile && isLandscape;
          const isMobileOrTablet = isMobile || isTablet;

          return (
            <a
              href={calendlyData.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute z-50"
              style={{
                top: pos.top,
                bottom: pos.bottom,
                left: pos.left,
                right: pos.right,
                transform: pos.transform
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #3182CE 0%, #1a5fa8 100%)",
                  borderRadius: isMobileLandscape ? "12px" : "16px",
                  padding: isMobileLandscape ? "8px 14px" : isMobileOrTablet ? "12px 20px" : "18px 32px",
                  boxShadow: "0 8px 32px rgba(49,130,206,0.5), 0 2px 8px rgba(0,0,0,0.3)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  animation: "calendly-pulse 2.5s ease-in-out infinite"
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "scale(1.06)";
                  e.currentTarget.style.boxShadow = "0 12px 40px rgba(49,130,206,0.7), 0 4px 12px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 8px 32px rgba(49,130,206,0.5), 0 2px 8px rgba(0,0,0,0.3)";
                }}
              >
                <span
                  style={{
                    fontSize: isMobileLandscape ? "13px" : isMobileOrTablet ? "16px" : "22px",
                    color: "#ffffff",
                    fontWeight: "800",
                    lineHeight: 1.1,
                    textAlign: "center"
                  }}
                >
                  {calendlyData.title}
                </span>
                <span
                  style={{
                    fontSize: isMobileLandscape ? "10px" : isMobileOrTablet ? "11px" : "12px",
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: "500"
                  }}
                >
                  {calendlyData.subtitle}
                </span>
              </div>
            </a>
          );
        })()}

        {/* CHAT WIDGET CONTAINER */}
        <div
          id="vf-chat-widget"
          ref={widgetContainerRef}
          className={`
          absolute z-40 transition-all duration-300
          ${resolution.width <= 768 ? 'rounded-2xl shadow-2xl' : ''}
        `}
          style={{
            width: widgetSize.width,
            height: widgetSize.height,
            left: `${widgetPosition.x}px`,
            top: `${widgetPosition.y}px`,
            opacity: widgetVisible ? 1 : 0,
            pointerEvents: widgetVisible ? 'auto' : 'none',
          }}
        />

        {/* RESIZE HANDLE ONLY */}
        {
          widgetVisible && (
            <div className="fixed" style={{
              left: `${widgetPosition.x + widgetSize.width - 16}px`,
              top: `${widgetPosition.y + widgetSize.height - 16}px`,
              width: '16px',
              height: '16px',
              zIndex: 99999,
              transform: `translateY(${widgetPosition.y}px)`,
            }}>
              <div className="w-4 h-4 bg-gray-400 cursor-nw-resize resize-handle" onMouseDown={handleResizeStart} style={{ pointerEvents: 'auto' }} title="Redimensionar">
                <div className="absolute bottom-1 right-1 w-2 h-2 bg-gray-600 rounded-full"></div>
              </div>
            </div>
          )
        }

        {/* MOBILE FLOATING CHAT BUTTON */}
        {
          resolution.width <= 768 && !widgetVisible && pages[currentPage]?.autoOpenWidget && (
            <button
              onClick={() => {
                setWidgetVisible(true);
                // La posición se gestiona ahora automáticamente por el useEffect maestro de getDevPos

              }}
              className="absolute bottom-6 right-6 z-50 w-14 h-14 bg-blue-600 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-blue-700 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          )
        }

        {/* INDICADOR DE RESOLUCIÓN Y PERFIL ACTIVO (DEBUG).
            Sólo en desarrollo: en producción lo veían los visitantes. */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed bottom-3 left-3 z-[99999] pointer-events-none select-none font-mono text-[11px] md:text-xs bg-black/85 backdrop-blur-md text-white px-3 py-1.5 rounded-lg border border-white/20 shadow-2xl flex items-center space-x-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span><strong>{resolution.width}x{resolution.height}</strong></span>
            <span className="text-white/40">|</span>
            <span>Ajuste: <strong className="text-emerald-300 font-bold">{deviceKey}</strong></span>
            <span className="text-white/40">|</span>
            <span className="text-white/70">{isLandscape ? 'Landscape' : 'Portrait'}</span>
          </div>
        )}

      </div >

      {/* FOOTER - flujo normal, al final del scroll */}
      <Footer isLandscape={isLandscape} isMobile={isMobile} />

      <style jsx>{`
        /* Keyframes por defecto para desktop grande (>1440px) */
        @keyframes zoom-in { from { transform: scale(0.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes zoom-out { from { transform: scale(1); opacity: 1; } to { transform: scale(2.5); opacity: 0; } }
        @keyframes zoom-in-from-far { from { transform: scale(2.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes zoom-out-to-far { from { transform: scale(1); opacity: 1; } to { transform: scale(0.4); opacity: 0; } }

        .animate-zoom-in { animation: zoom-in 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .animate-zoom-out { animation: zoom-out 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .animate-zoom-in-from-far { animation: zoom-in-from-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .animate-zoom-out-to-far { animation: zoom-out-to-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }

        /* Intensificación de zoom para móviles y tablets (iPad & iPad Pro) */
        @media (max-width: 1366px) {
          @keyframes zoom-in { from { transform: scale(0.25); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes zoom-out { from { transform: scale(1); opacity: 1; } to { transform: scale(5.0); opacity: 0; } }
          @keyframes zoom-in-from-far { from { transform: scale(5.0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes zoom-out-to-far { from { transform: scale(1); opacity: 1; } to { transform: scale(0.2); opacity: 0; } }

          .animate-zoom-in { animation: zoom-in 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-out { animation: zoom-out 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-in-from-far { animation: zoom-in-from-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-out-to-far { animation: zoom-out-to-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        }

        /* Keyframes específicos para Mac (1366-1440px) */
        @media (min-width: 1366px) and (max-width: 1440px) {
          @keyframes zoom-in { from { transform: scale(0.45); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes zoom-out { from { transform: scale(1); opacity: 1; } to { transform: scale(2.2); opacity: 0; } }
          @keyframes zoom-in-from-far { from { transform: scale(2.2); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes zoom-out-to-far { from { transform: scale(1); opacity: 1; } to { transform: scale(0.45); opacity: 0; } }

          .animate-zoom-in { animation: zoom-in 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-out { animation: zoom-out 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-in-from-far { animation: zoom-in-from-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
          .animate-zoom-out-to-far { animation: zoom-out-to-far 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        }

        @keyframes dramatic-zoom-in {
          0% { transform: scale(0.3) translateY(40px); opacity: 0; filter: blur(4px); }
          70% { transform: scale(1.12) translateY(-10px); opacity: 1; filter: blur(0); }
          100% { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
        }

        @keyframes dramatic-zoom-in-subtitle {
          0% { transform: scale(0.4) translateY(50px); opacity: 0; filter: blur(3px); }
          70% { transform: scale(1.1) translateY(-5px); opacity: 0.9; filter: blur(0); }
          100% { transform: scale(1) translateY(0); opacity: 0.8; filter: blur(0); }
        }

        .animate-dramatic-title {
          animation: dramatic-zoom-in 1.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-dramatic-subtitle {
          animation: dramatic-zoom-in-subtitle 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.4), 0 0 30px rgba(16, 185, 129, 0.2);
          }
          50% {
            box-shadow: 0 0 25px rgba(16, 185, 129, 0.6), 0 0 50px rgba(16, 185, 129, 0.3), 0 0 70px rgba(16, 185, 129, 0.1);
          }
        }

        @keyframes calendly-pulse {
          0%, 100% {
            box-shadow: 0 8px 32px rgba(49, 130, 206, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3);
          }
          50% {
            box-shadow: 0 8px 48px rgba(49, 130, 206, 0.8), 0 4px 16px rgba(0, 0, 0, 0.35);
          }
        }

        @keyframes spin-once {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }

        .group:hover .animate-spin-once {
          animation: spin-once 0.6s ease-in-out;
        }

        .vfrc-chat {
          border-radius: 16px !important;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25) !important;
        }
        
        @media (max-width: 768px) {
          .vfrc-chat {
            max-height: 48vh !important;
            height: 48vh !important;
            margin-bottom: 0 !important;
            border-radius: 14px !important;
          }
          .vfrc-header {
            height: 36px !important;
            padding: 4px 10px !important;
          }
          .vfrc-message-list {
            padding: 6px !important;
          }
          .vfrc-message, .vfrc-system-response, .vfrc-user-response, .vfrc-prompt-action {
            font-size: 0.8125rem !important;
            line-height: 1.25 !important;
            padding: 5px 8px !important;
          }
          .vfrc-footer {
            padding: 4px 8px !important;
          }
          .vfrc-input-container textarea, .vfrc-input-container input {
            font-size: 0.8125rem !important;
            padding: 6px 10px !important;
          }
        }

        .vfrc-header {
          height: 48px !important;
        }
        .vfrc-message-list {
          padding: 8px !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        `}</style>
    </div>
  );
};

export default HomePage;